import React from 'react';
import { getCableColorForSVG, preprocessTextForWrapping, splitText } from '../../utils/cableLogic';
import { renderTerminalSchematic } from '../../utils/terminalRenderLogic';
import { COLOR_MAP } from '../../constants/colorConstants';

// Funzione per verificare se è un cavo di terra
const isEarthWire = (colore) => {
    return colore === 'GI/VE' || colore === 'YE/GN';
};

// Funzione helper per renderizzare linee con bordo per cavi bianchi
const renderCableLine = ({ x1, y1, x2, y2, colore, ...props }) => {
    const color = getCableColorForSVG(colore);
    const isWhite = color === '#FFFFFF';
    
    if (isWhite) {
        const baseStrokeWidth = typeof props.strokeWidth === 'string' ? parseInt(props.strokeWidth) : props.strokeWidth;
        return (
            <>
                {/* Bordo scuro per visibilità (sotto) */}
                <line
                    x1={x1} y1={y1} x2={x2} y2={y2}
                    stroke="#666666"
                    strokeWidth={baseStrokeWidth + 1}
                    {...props}
                />
                {/* Linea bianca principale (sopra) */}
                <line
                    x1={x1} y1={y1} x2={x2} y2={y2}
                    stroke={color}
                    {...props}
                />
            </>
        );
    } else {
        return (
            <line
                x1={x1} y1={y1} x2={x2} y2={y2}
                stroke={color}
                {...props}
            />
        );
    }
};

// Componente per visualizzazione cavi raggruppati in una guaina
const SheathCableVisualization = ({ cables, sheathLabel, hoveredCable, setHoveredCable, isMobile, settings }) => {
    // Controllo che ci siano cavi
    if (!cables || cables.length === 0) {
        return null;
    }

    const svgWidth = isMobile ? 400 : 800;
    const svgHeight = Math.max(200, 80 + (cables.length * 60)); // Altezza dinamica
    const mainCableY = svgHeight / 2;

    // Dimensioni rettangolo guaina (centrale)
    const sheathWidth = 300;
    const sheathHeight = 40;
    const sheathX = (svgWidth - sheathWidth) / 2;
    const sheathRect = {
        x: sheathX,
        y: mainCableY - sheathHeight / 2,
        width: sheathWidth,
        height: sheathHeight
    };

    // Spazio per i cavi a sinistra e destra
    const cableLeftX = 120;
    const cableRightX = svgWidth - 120;
    const cableSpacing = Math.min(50, (svgHeight - 100) / Math.max(1, cables.length - 1));
    const cableStartY = svgHeight / 2 - ((cables.length - 1) * cableSpacing) / 2;

    return (
        <svg
            className="w-full"
            viewBox={`0 0 ${svgWidth} ${svgHeight}`}
            xmlns="http://www.w3.org/2000/svg"
        >
            {/* PRIMA: Rendering Cavi (sotto) */}
            {/* Calcola lunghezze massime per scala proporzionale (come nei multipolari) */}
            {(() => {
                const lunghezzeUniche = [...new Set(cables.map(c => c.lunghezza || 0))].filter(l => l > 0).sort((a, b) => a - b);
                const maxLunghezza = Math.max(...lunghezzeUniche);
                const maxScale = 80; // Lunghezza massima visiva in pixel

                return cables.map((cable, index) => {
                    const cableY = cableStartY + (index * cableSpacing);
                    const isHovered = hoveredCable === cable.originalIndex;

                    // ✅ CALCOLI IDENTICI AI MULTIPOLARI - LATO A (sinistra)
                    const cableExitXA = sheathRect.x;
                    const cableExitYA = sheathRect.y + (sheathRect.height / (cables.length + 1)) * (index + 1);

                    // Lunghezza proporzionale - IDENTICO AI MULTIPOLARI
                    const lunghezzaProporzionale = (cable.lunghezza || 0) / Math.max(maxLunghezza, 1);
                    const lunghezzaVisuale = lunghezzaProporzionale * maxScale;

                    const horizontalStartXA = cableLeftX + 40;
                    const horizontalEndXA = horizontalStartXA - lunghezzaVisuale; // Fine proporzionale

                    // ✅ CALCOLI IDENTICI AI MULTIPOLARI - LATO B (destra)
                    const cableExitXB = sheathRect.x + sheathRect.width;
                    const cableExitYB = sheathRect.y + (sheathRect.height / (cables.length + 1)) * (index + 1);

                    const horizontalStartXB = cableRightX - 40;
                    const horizontalEndXB = horizontalStartXB + lunghezzaVisuale; // Fine proporzionale

                    const opacity = hoveredCable !== null && !isHovered ? 0.3 : 1;

                return (
                    <g key={`cable-${index}`}>
                        {/* LATO A (SINISTRA) */}
                        <g
                            onMouseEnter={() => setHoveredCable(cable.originalIndex)}
                            onMouseLeave={() => setHoveredCable(null)}
                            style={{ cursor: 'pointer' }}
                        >
                            {/* ✅ Linea obliqua dal rettangolo guaina verso il punto fisso */}
                            {(() => {
                                const isGialloVerde = isEarthWire(cable.colore);
                                const commonProps = {
                                    strokeWidth: "3",
                                    strokeLinecap: "round",
                                    opacity: isHovered ? 1 : 0.8
                                };

                                if (isGialloVerde) {
                                    return (
                                        <>
                                            <line
                                                x1={cableExitXA} y1={cableExitYA} x2={horizontalStartXA} y2={cableY}
                                                stroke={COLOR_MAP['GIALLO']}
                                                strokeDasharray="8 8"
                                                strokeDashoffset={0}
                                                {...commonProps}
                                            />
                                            <line
                                                x1={cableExitXA} y1={cableExitYA} x2={horizontalStartXA} y2={cableY}
                                                stroke={COLOR_MAP['VERDE']}
                                                strokeDasharray="8 8"
                                                strokeDashoffset={8}
                                                {...commonProps}
                                            />
                                        </>
                                    );
                                } else {
                                    return renderCableLine({
                                        x1: cableExitXA,
                                        y1: cableExitYA,
                                        x2: horizontalStartXA,
                                        y2: cableY,
                                        colore: cable.colore,
                                        ...commonProps
                                    });
                                }
                            })()}

                            {/* ✅ Tratto orizzontale */}
                            {(() => {
                                const isGialloVerde = isEarthWire(cable.colore);
                                const commonProps = {
                                    strokeWidth: "3",
                                    strokeLinecap: "round",
                                    opacity: isHovered ? 1 : 0.8
                                };

                                if (isGialloVerde) {
                                    return (
                                        <>
                                            <line
                                                x1={horizontalStartXA} y1={cableY} x2={horizontalEndXA} y2={cableY}
                                                stroke={COLOR_MAP['GIALLO']}
                                                strokeDasharray="8 8"
                                                strokeDashoffset={0}
                                                {...commonProps}
                                            />
                                            <line
                                                x1={horizontalStartXA} y1={cableY} x2={horizontalEndXA} y2={cableY}
                                                stroke={COLOR_MAP['VERDE']}
                                                strokeDasharray="8 8"
                                                strokeDashoffset={8}
                                                {...commonProps}
                                            />
                                        </>
                                    );
                                } else {
                                    return renderCableLine({
                                        x1: horizontalStartXA,
                                        y1: cableY,
                                        x2: horizontalEndXA,
                                        y2: cableY,
                                        colore: cable.colore,
                                        ...commonProps
                                    });
                                }
                            })()}

                            {/* ✅ Terminale A alla fine del tratto orizzontale */}
                            {renderTerminalSchematic(
                                cable.terminaleA,
                                horizontalEndXA,
                                cableY,
                                isMobile ? 0.03 : 0.04,
                                'A',
                                180
                            )}

                            {/* Etichetta siglatura A - non mostrare per cavi di terra */}
                            {cable.siglatura && !isEarthWire(cable.colore) && (
                                <g>
                                    <rect
                                        x={horizontalEndXA + 40 - 6}
                                        y={cableY - 8}
                                        width={(cable.siglatura.length * 8) + 12}
                                        height={16}
                                        rx={3}
                                        fill="white"
                                        stroke="#D1D5DB"
                                        strokeWidth={1}
                                        filter="drop-shadow(0 1px 3px rgba(0, 0, 0, 0.2))"
                                    />
                                    <text
                                        x={horizontalEndXA + 40}
                                        y={cableY + 1}
                                        className="text-xs font-bold fill-gray-800"
                                        textAnchor="start"
                                        dominantBaseline="middle"
                                    >
                                        {cable.siglatura}
                                    </text>
                                </g>
                            )}

                            {/* Collegamento A */}
                            <text
                                x={horizontalEndXA - 30}
                                y={cableY}
                                className="text-xs font-medium"
                                textAnchor="end"
                                dominantBaseline="middle"
                                fontSize="10"
                                fill="#374151"
                            >
                                {splitText(preprocessTextForWrapping(cable.collegamentoA || '—'), 8).map((line, i) => (
                                    <tspan
                                        key={i}
                                        x={horizontalEndXA - 35}
                                        dy={i === 0 ? 0 : 12}
                                    >
                                        {line}
                                    </tspan>
                                ))}
                            </text>
                        </g>

                        {/* LATO B (DESTRA) */}
                        <g
                            onMouseEnter={() => setHoveredCable(cable.originalIndex)}
                            onMouseLeave={() => setHoveredCable(null)}
                            style={{ cursor: 'pointer' }}
                        >
                            {/* ✅ Linea obliqua dal rettangolo guaina verso il punto fisso */}
                            {(() => {
                                const isGialloVerde = isEarthWire(cable.colore);
                                const commonProps = {
                                    strokeWidth: "3",
                                    strokeLinecap: "round",
                                    opacity: isHovered ? 1 : 0.8
                                };

                                if (isGialloVerde) {
                                    return (
                                        <>
                                            <line
                                                x1={cableExitXB} y1={cableExitYB} x2={horizontalStartXB} y2={cableY}
                                                stroke={COLOR_MAP['GIALLO']}
                                                strokeDasharray="8 8"
                                                strokeDashoffset={0}
                                                {...commonProps}
                                            />
                                            <line
                                                x1={cableExitXB} y1={cableExitYB} x2={horizontalStartXB} y2={cableY}
                                                stroke={COLOR_MAP['VERDE']}
                                                strokeDasharray="8 8"
                                                strokeDashoffset={8}
                                                {...commonProps}
                                            />
                                        </>
                                    );
                                } else {
                                    return renderCableLine({
                                        x1: cableExitXB,
                                        y1: cableExitYB,
                                        x2: horizontalStartXB,
                                        y2: cableY,
                                        colore: cable.colore,
                                        ...commonProps
                                    });
                                }
                            })()}
                            
                            {/* ✅ Tratto orizzontale */}
                            {(() => {
                                const isGialloVerde = isEarthWire(cable.colore);
                                const commonProps = {
                                    strokeWidth: "3",
                                    strokeLinecap: "round",
                                    opacity: isHovered ? 1 : 0.8
                                };

                                if (isGialloVerde) {
                                    return (
                                        <>
                                            <line
                                                x1={horizontalStartXB} y1={cableY} x2={horizontalEndXB} y2={cableY}
                                                stroke={COLOR_MAP['GIALLO']}
                                                strokeDasharray="8 8"
                                                strokeDashoffset={0}
                                                {...commonProps}
                                            />
                                            <line
                                                x1={horizontalStartXB} y1={cableY} x2={horizontalEndXB} y2={cableY}
                                                stroke={COLOR_MAP['VERDE']}
                                                strokeDasharray="8 8"
                                                strokeDashoffset={8}
                                                {...commonProps}
                                            />
                                        </>
                                    );
                                } else {
                                    return renderCableLine({
                                        x1: horizontalStartXB,
                                        y1: cableY,
                                        x2: horizontalEndXB,
                                        y2: cableY,
                                        colore: cable.colore,
                                        ...commonProps
                                    });
                                }
                            })()}

                            {/* ✅ Terminale B alla fine del tratto orizzontale */}
                            {renderTerminalSchematic(
                                cable.terminaleB,
                                horizontalEndXB,
                                cableY,
                                isMobile ? 0.03 : 0.04,
                                'B',
                                0
                            )}

                            {/* Etichetta siglatura B - non mostrare per cavi di terra */}
                            {cable.siglatura && !isEarthWire(cable.colore) && (
                                <g>
                                    <rect
                                        x={horizontalEndXB - 40 - (cable.siglatura.length * 8) - 6}
                                        y={cableY - 8}
                                        width={(cable.siglatura.length * 8) + 12}
                                        height={16}
                                        rx={3}
                                        fill="white"
                                        stroke="#D1D5DB"
                                        strokeWidth={1}
                                        filter="drop-shadow(0 1px 3px rgba(0, 0, 0, 0.2))"
                                    />
                                    <text
                                        x={horizontalEndXB - 40}
                                        y={cableY + 1}
                                        className="text-xs font-bold fill-gray-800"
                                        textAnchor="end"
                                        dominantBaseline="middle"
                                    >
                                        {cable.siglatura}
                                    </text>
                                </g>
                            )}

                            {/* Collegamento B */}
                            <text
                                x={horizontalEndXB + 30}
                                y={cableY}
                                className="text-xs font-medium"
                                textAnchor="start"
                                dominantBaseline="middle"
                                fontSize="10"
                                fill="#374151"
                            >
                                {splitText(preprocessTextForWrapping(cable.collegamentoB || '—'), 8).map((line, i) => (
                                    <tspan
                                        key={i}
                                        x={horizontalEndXB + 35}
                                        dy={i === 0 ? 0 : 12}
                                    >
                                        {line}
                                    </tspan>
                                ))}
                            </text>
                        </g>
                    </g>
                );
                });
            })()}

            {/* POI: Rettangolo centrale della guaina (sopra i cavi) */}
            <rect
                x={sheathRect.x}
                y={sheathRect.y}
                width={sheathRect.width}
                height={sheathRect.height}
                fill="#1f2937"
                stroke="#111827"
                strokeWidth="2"
                rx="5"
            />

            {/* Etichetta guaina al centro */}
            <text
                x={sheathRect.x + sheathRect.width / 2}
                y={sheathRect.y + 2 + sheathRect.height / 2}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize="14"
                fontWeight="600"
                fill="white"
            >
                {sheathLabel}
            </text>

            {/* Indici dei cavi sopra la guaina */}
            {(() => {
                const indexSpacing = 40; // Spazio tra i cerchi
                const centerX = sheathRect.x + sheathRect.width / 2;
                const startX = centerX - ((cables.length - 1) * indexSpacing) / 2;
                const indexY = sheathRect.y - 25; // 25px sopra il rettangolo guaina

                return cables.map((cable, index) => {
                    const indexX = startX + (index * indexSpacing);
                    
                    return (
                        <g key={`index-${index}`}>
                            <circle
                                cx={indexX}
                                cy={indexY}
                                r={15}
                                fill="#1F2937"
                                opacity={0.9}
                            />
                            <text
                                className="cable-number"
                                x={indexX}
                                y={indexY + 1}
                                fontSize={14}
                                fill="white"
                                textAnchor="middle"
                                dominantBaseline="middle"
                            >
                                #{cable.originalIndex + 1}
                            </text>
                        </g>
                    );
                });
            })()}
        </svg>
    );
};

export default SheathCableVisualization;

